/**
 * Pass API client — separate axios instance pointing at /api/v2/pass.
 *
 * Two auth modes coexist:
 *  - passHttp:           JWT business (admin/business endpoints + Stripe config + payouts admin).
 *  - passCustomerHttp:   customer_token query param (affiliate-facing endpoints).
 */

import axios from 'axios';
import { API_URL } from '../config';

const PASS_BASE = `${API_URL}/api/v2/pass`;

const passHttp = axios.create({
  baseURL: PASS_BASE,
  headers: { 'Content-Type': 'application/json' },
});

passHttp.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Customer-token client: auto-injects ?customer_token=... in every request
const passCustomerHttp = axios.create({
  baseURL: PASS_BASE,
  headers: { 'Content-Type': 'application/json' },
});

passCustomerHttp.interceptors.request.use((config) => {
  const token = localStorage.getItem('customer_token');
  if (token) {
    config.params = { ...(config.params || {}), customer_token: token };
  }
  return config;
});

export function getCustomerToken() {
  return localStorage.getItem('customer_token');
}

// Build a URL with customer_token in query — for <img src=...> and <a href=...>
function buildCustomerUrl(path, extraParams = {}) {
  const token = getCustomerToken();
  const search = new URLSearchParams({ ...extraParams, ...(token ? { customer_token: token } : {}) });
  return `${PASS_BASE}${path}?${search.toString()}`;
}

// --- Affiliate attribution helpers (localStorage with 48h TTL) ---

const ATTR_KEY = 'pass_affiliate_attribution';
const ATTR_TTL_MS = 48 * 60 * 60 * 1000;

export function rememberAffiliate(slug, code) {
  try {
    localStorage.setItem(
      ATTR_KEY,
      JSON.stringify({ slug, code, expires_at: Date.now() + ATTR_TTL_MS })
    );
  } catch (_) {}
}

export function getRememberedAffiliate() {
  try {
    const raw = localStorage.getItem(ATTR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expires_at || parsed.expires_at < Date.now()) {
      localStorage.removeItem(ATTR_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

// --- Public ---
export const passPublic = {
  affiliate: (slug) => passHttp.get(`/public/affiliate/${slug}`),
  offer: (offerSlug) => passHttp.get(`/public/offers/${offerSlug}`),
  availability: (offerSlug, date) => passHttp.get(`/public/offers/${offerSlug}/availability`, { params: date ? { date } : undefined }),
  marketplaceOffers: (params) => passHttp.get('/public/marketplace-offers', { params }),
  cities: () => passHttp.get('/public/cities'),
  guides: (params) => passHttp.get('/public/guides', { params }),
  guide: (slug) => passHttp.get(`/public/guides/${slug}`),
};

// --- Reservations (no auth for claim-free + ticket lookup) ---
export const passReservations = {
  claimFree: (payload) => passHttp.post('/reservations/claim-free', payload),
  getTicket: (qrToken) => passHttp.get(`/reservations/ticket/${qrToken}`),
};

// --- Affiliates (customer-token auth) ---
export const passAffiliates = {
  register: (data) => passCustomerHttp.post('/affiliates/register', data),
  me: () => passCustomerHttp.get('/affiliates/me'),
  updateMe: (data) => passCustomerHttp.put('/affiliates/me', data),
  qrImageUrl: () => buildCustomerUrl('/affiliates/me/qr-image'),
  printMaterialUrl: ({ size = 'letter', template = 'taxi_01' } = {}) =>
    buildCustomerUrl('/affiliates/me/print-material', { size, template }),
  commissions: () => passCustomerHttp.get('/affiliates/me/commissions'),
  availableOffers: () => passCustomerHttp.get('/affiliates/me/available-offers'),
  availableGuides: () => passCustomerHttp.get('/affiliates/me/available-guides'),
  updateCatalog: (data) => passCustomerHttp.put('/affiliates/me/catalog', data),
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return passCustomerHttp.post('/affiliates/me/avatar', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// --- Business ---
export const passBusiness = {
  createOffer: (data) => passHttp.post('/business/offers', data),
  updateOffer: (id, data) => passHttp.put(`/business/offers/${id}`, data),
  getOffer: (id) => passHttp.get(`/business/offers/${id}`),
  listOffers: (params) => passHttp.get('/business/offers', { params }),
  listReservations: () => passHttp.get('/business/reservations'),
  pauseOffer: (id) => passHttp.post(`/business/offers/${id}/pause`),
  activateOffer: (id) => passHttp.post(`/business/offers/${id}/activate`),
  archiveOffer: (id) => passHttp.post(`/business/offers/${id}/archive`),
  templates: () => passHttp.get('/business/offers/templates'),
};

// --- Check-in ---
export const passCheckins = {
  scan: (qr_token) => passHttp.post('/checkins/scan', { qr_token }),
  confirm: (payload) => passHttp.post('/checkins/confirm', payload),
};

// --- Admin ---
export const passAdmin = {
  summary: () => passHttp.get('/admin/summary'),
  affiliates: () => passHttp.get('/admin/affiliates'),
  commissions: (params) => passHttp.get('/admin/commissions', { params }),
  getStripeConfig: () => passHttp.get('/admin/stripe-config'),
  updateStripeConfig: (data) => passHttp.put('/admin/stripe-config', data),
  testStripeConfig: () => passHttp.post('/admin/stripe-config/test'),
  listPayouts: (params) => passHttp.get('/admin/payouts', { params }),
  decidePayout: (id, payload) => passHttp.post(`/admin/payouts/${id}/decide`, payload),
  // Sprint 7: settlements + reports
  listSettlements: (params) => passHttp.get('/admin/business-settlements', { params }),
  generateSettlements: (payload) => passHttp.post('/admin/business-settlements/generate', payload),
  decideSettlement: (id, payload) => passHttp.post(`/admin/business-settlements/${id}/decide`, payload),
  reportSummary: (params) => passHttp.get('/admin/reports/summary', { params }),
  reportByCategory: (params) => passHttp.get('/admin/reports/by-category', { params }),
  reportByCity: (params) => passHttp.get('/admin/reports/by-city', { params }),
  reportTopAffiliates: (params) => passHttp.get('/admin/reports/top-affiliates', { params }),
  reportTopBusinesses: (params) => passHttp.get('/admin/reports/top-businesses', { params }),
  // Sprint 7.5: guides
  listGuides: (params) => passHttp.get('/admin/guides', { params }),
  getGuide: (id) => passHttp.get(`/admin/guides/${id}`),
  createGuide: (data) => passHttp.post('/admin/guides', data),
  updateGuide: (id, data) => passHttp.put(`/admin/guides/${id}`, data),
  deleteGuide: (id) => passHttp.delete(`/admin/guides/${id}`),
};

// --- Payments (public) ---
export const passPayments = {
  createCheckoutSession: (payload) => passHttp.post('/payments/checkout-session', payload),
};

// --- Affiliate wallet (customer-token auth) ---
export const passWallet = {
  me: () => passCustomerHttp.get('/affiliates/me/wallet'),
  requestPayout: (payload) => passCustomerHttp.post('/affiliates/me/payouts/request', payload),
};

export default {
  passPublic,
  passReservations,
  passAffiliates,
  passBusiness,
  passCheckins,
  passAdmin,
  rememberAffiliate,
  getRememberedAffiliate,
};
